import { either, function as fn, option } from 'fp-ts'
import { Epic } from 'redux-observable'
import { concat, defer, EMPTY, forkJoin, from, iif, Observable, of } from 'rxjs'
import { catchError, concatMap, defaultIfEmpty, filter, map, mapTo, switchMap, withLatestFrom } from 'rxjs/operators'
import { DeepReadonly, DeepPartial } from 'ts-essentials'
import { isActionOf, PayloadAction, TypeConstant } from 'typesafe-actions'
import { getAuthenticationApi } from '@/api/api_di'
import {
  ServiceRegisterResponse,
  ServiceRegisterResponseError,
  ServiceGetSaltResponse,
  ServiceGetSaltResponseError,
  ServiceLogInResponse,
  ServiceLogInResponseError,
  ServiceProvideOtpResponse,
  ServiceProvideOtpResponseError,
  ServiceUserData
} from '@/api/definitions'
import { getSodiumClient, MasterKeyDerivatives } from '@/cryptography/sodium_client'
import { Key } from '@/redux/domain'
import { createDisplayExceptionsEpic } from '@/redux/exceptions'
import {
  cancel,
  exception,
  failure,
  indicator,
  isSignalFailure,
  errorToMessage,
  success,
  isActionSuccess2,
  isActionSuccess,
  isSignalError
} from '@/redux/flow_signal'
import { defaultMailVerification, localOtpTokenFailure, remoteCredentialsMismatchLocal } from '@/redux/modules/user/account/actions'
import { NIL_KEY_ID } from '@/redux/modules/user/keys/actions'
import { fromKeyProto } from '@/redux/modules/user/keys/converters'
import { RootAction } from '@/redux/root_action'
import { RootState } from '@/redux/root_reducer'
import {
  RegistrationFlowIndicator,
  register,
  registrationReset,
  registrationSignal,
  logInViaApi,
  authnViaApiReset,
  authnViaApiSignal,
  AuthnViaApiFlowIndicator,
  logInViaDepot,
  authnViaDepotReset,
  authnViaDepotSignal,
  AuthnViaDepotFlowIndicator,
  AuthnViaDepotFlowError,
  initiateBackgroundAuthn,
  AuthnViaApiSignal,
  backgroundRemoteAuthnSignal,
  remoteAuthnComplete,
  AuthnViaApiParams,
  authnOtpProvisionReset,
  authnOtpProvisionSignal,
  AuthnOtpProvisionSignal,
  provideOtp,
  AuthnOtpProvisionFlowIndicator,
  UserData,
  backgroundOtpProvisionSignal,
  backgroundAuthnError,
  AuthnInputKind,
  AuthnInput
} from './actions'

export const registrationEpic: Epic<RootAction, RootAction, RootState> = (action$) => action$.pipe(
  filter(isActionOf([register, registrationReset])),
  switchMap((action) => {
    if (isActionOf(register, action)) {
      return concat(
        of(registrationSignal(indicator(RegistrationFlowIndicator.GENERATING_PARAMETRIZATION))),
        from(getSodiumClient().generateNewParametrization()).pipe(
          switchMap((parametrization) => concat(
            of(registrationSignal(indicator(RegistrationFlowIndicator.COMPUTING_MASTER_KEY_DERIVATIVES))),
            from(getSodiumClient().computeAuthDigestAndEncryptionKey(parametrization, action.payload.password)).pipe(
              switchMap(({ authDigest, encryptionKey }) => concat(
                of(registrationSignal(indicator(RegistrationFlowIndicator.MAKING_REQUEST))),
                from(getAuthenticationApi().authenticationRegister({
                  username: action.payload.username,
                  salt: parametrization,
                  digest: authDigest,
                  mail: action.payload.mail,
                  captchaToken: action.payload.captchaToken
                })).pipe(
                  switchMap((response: ServiceRegisterResponse) => {
                    switch (response.error) {
                      case ServiceRegisterResponseError.NONE:
                        return of(registrationSignal(success({
                          userId: response.userUid!,
                          username: action.payload.username,
                          password: action.payload.password,
                          parametrization,
                          authDigest,
                          encryptionKey,
                          sessionKey: response.sessionKey!,
                          mailTokenId: response.mailTokenUid!
                        })))
                      default:
                        return of(registrationSignal(failure(response.error!)))
                    }
                  })
                )
              ))
            )
          ))
        )
      ).pipe(
        catchError((error) => of(registrationSignal(exception(errorToMessage(error)))))
      )
    } else if (isActionOf(registrationReset, action)) {
      return of(registrationSignal(cancel()))
    }
    return EMPTY
  })
)

export const displayRegistrationExceptionsEpic = createDisplayExceptionsEpic(registrationSignal)

const decodeUserData = (encryptionKey: string, userData: ServiceUserData): Observable<UserData> => {
  return forkJoin(userData.userKeys!.map(
    fromKeyProto(encryptionKey)
  )).pipe(
    defaultIfEmpty(<Key[]>[]),
    switchMap((userKeys) => {
      const mv = userData.mailVerification
      return of({
        userId: userData.userUid!,
        sessionKey: userData.sessionKey!,
        featurePrompts: userData.featurePrompts!,
        mailVerification: mv ? {
          required: mv.required!,
          tokenId: mv.tokenUid!
        } : defaultMailVerification(),
        mail: userData.mail || null,
        userKeys
      })
    })
  )
}

const logInRpc = <T extends TypeConstant>(
  signalCreator: (payload: DeepReadonly<AuthnViaApiSignal>) => PayloadAction<T, DeepReadonly<AuthnViaApiSignal>> & RootAction,
  { username, authnInput, parametrization, authDigest, encryptionKey }: {
    username: string;
    authnInput: AuthnInput;
    parametrization: string;
    authDigest: string;
    encryptionKey: string
  }
): Observable<RootAction> => concat(
    of(signalCreator(indicator(AuthnViaApiFlowIndicator.MAKING_REQUEST))),
    from(getAuthenticationApi().authenticationLogIn({
      username: username,
      digest: authDigest
    })).pipe(
      switchMap((logInResponse: ServiceLogInResponse) => {
        switch (logInResponse.error) {
          case ServiceLogInResponseError.NONE: {
            const params: AuthnViaApiParams = {
              username,
              authnInput,
              parametrization,
              authDigest,
              encryptionKey
            }
            if (logInResponse.userData === null) {
              return of(signalCreator(success({
                ...params,
                content: either.left({
                  authnKey: logInResponse.otpContext!.authnKey!,
                  attemptsLeft: logInResponse.otpContext!.attemptsLeft!
                })
              })))
            }
            return concat(
              of(signalCreator(indicator(AuthnViaApiFlowIndicator.DECRYPTING_DATA))),
              decodeUserData(encryptionKey, logInResponse.userData!).pipe(
                switchMap((userData) => of(signalCreator(success({
                  ...params,
                  content: either.right(userData)
                }))))
              )
            )
          }
          case ServiceLogInResponseError.RATELIMITED:
            return of(signalCreator(exception('Rate limited')))
          default:
            return of(signalCreator(failure(logInResponse.error!)))
        }
      })
    )
  )

const apiAuthn = <T extends TypeConstant>(
  signalCreator: (payload: DeepReadonly<AuthnViaApiSignal>) => PayloadAction<T, DeepReadonly<AuthnViaApiSignal>> & RootAction,
  { username, password }: { username: string; password: string }
): Observable<RootAction> => {
  return concat(
    of(signalCreator(indicator(AuthnViaApiFlowIndicator.RETRIEVING_PARAMETRIZATION))),
    from(getAuthenticationApi().authenticationFetchSalt({ username })).pipe(
      switchMap((getSaltResponse: ServiceGetSaltResponse) => {
        switch (getSaltResponse.error) {
          case ServiceGetSaltResponseError.NONE:
            return concat(
              of(signalCreator(indicator(AuthnViaApiFlowIndicator.COMPUTING_MASTER_KEY_DERIVATIVES))),
              from(getSodiumClient().computeAuthDigestAndEncryptionKey(getSaltResponse.salt!, password)).pipe(
                switchMap(({ authDigest, encryptionKey }) => logInRpc(signalCreator, {
                  username,
                  authnInput: { kind: AuthnInputKind.PASSWORD, password },
                  parametrization: getSaltResponse.salt!,
                  authDigest,
                  encryptionKey
                }))
              )
            )
          default:
            return of(signalCreator(failure(getSaltResponse.error!)))
        }
      })
    )
  ).pipe(
    catchError((error) => of(signalCreator(exception(errorToMessage(error)))))
  )
}

export const logInViaApiEpic: Epic<RootAction, RootAction, RootState> = (action$) => action$.pipe(
  filter(isActionOf([logInViaApi, authnViaApiReset])),
  switchMap((action) => {
    if (isActionOf(logInViaApi, action)) {
      return apiAuthn(authnViaApiSignal, action.payload)
    } else if (isActionOf(authnViaApiReset, action)) {
      return of(authnViaApiSignal(cancel()))
    }
    return EMPTY
  })
)

export const displayAuthnViaApiExceptionsEpic = createDisplayExceptionsEpic(authnViaApiSignal)

const otpProvision = <T extends TypeConstant>(
  { credentialParams, authnKey, otp, yieldTrustedToken }: {
    credentialParams: AuthnViaApiParams;
    authnKey: string;
    otp: string;
    yieldTrustedToken: boolean;
  },
  signalCreator: (payload: DeepReadonly<AuthnOtpProvisionSignal>) => PayloadAction<T, DeepReadonly<AuthnOtpProvisionSignal>> & RootAction
): Observable<RootAction> => {
  return concat(
    of(signalCreator(indicator(AuthnOtpProvisionFlowIndicator.MAKING_REQUEST))),
    from(getAuthenticationApi().authenticationProvideOtp({ authnKey, otp, yieldTrustedToken })).pipe(
      switchMap((response: ServiceProvideOtpResponse) => {
        switch (response.error) {
          case ServiceProvideOtpResponseError.NONE:
            return concat(
              of(signalCreator(indicator(AuthnOtpProvisionFlowIndicator.DECRYPTING_DATA))),
              decodeUserData(credentialParams.encryptionKey, response.userData!).pipe(
                switchMap((userData) => of(signalCreator(success({
                  credentialParams,
                  trustedToken: option.fromNullable(response.trustedToken),
                  userData
                }))))
              )
            )
          default:
            return of(signalCreator(failure({
              error: response.error!,
              attemptsLeft: response.attemptsLeft!
            })))
        }
      })
    )
  ).pipe(
    catchError((error) => of(signalCreator(exception(errorToMessage(error)))))
  )
}

export const provideOtpEpic: Epic<RootAction, RootAction, RootState> = (action$) => action$.pipe(
  filter(isActionOf([provideOtp, authnOtpProvisionReset])),
  switchMap((action) => {
    if (isActionOf(provideOtp, action)) {
      return otpProvision(action.payload, authnOtpProvisionSignal)
    } else if (isActionOf(authnOtpProvisionReset, action)) {
      return of(authnOtpProvisionSignal(cancel()))
    }
    return EMPTY
  })
)

export const displayAuthnOtpProvisionExceptionsEpic = createDisplayExceptionsEpic(authnOtpProvisionSignal)

const normalizeParentId = (id?: string): string => {
  if (!id) {
    return NIL_KEY_ID
  }
  // Leftover from the migration to UUIDs.
  if (id === '0') {
    return NIL_KEY_ID
  }
  return id
}

export const logInViaDepotEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) => action$.pipe(
  filter(isActionOf([logInViaDepot, authnViaDepotReset])),
  withLatestFrom(state$),
  switchMap(([action, state]) => {
    if (isActionOf(logInViaDepot, action)) {
      const { credentials } = state.depot
      if (credentials === null || credentials.username !== action.payload.username) {
        return of(authnViaDepotSignal(failure(AuthnViaDepotFlowError.INVALID_CREDENTIALS)))
      }
      const masterKeyDerivativesObservable: Observable<MasterKeyDerivatives> = defer(async () => {
        const { authnInput } = action.payload
        if (authnInput.kind === AuthnInputKind.PASSWORD) {
          return await getSodiumClient().computeAuthDigestAndEncryptionKey(
            credentials.salt,
            authnInput.password
          )
        }
        if (authnInput.kind === AuthnInputKind.WEB_AUTHN) {
          const { webAuthnResult, webAuthnEncryptedLocalDerivatives } = state.depot
          if (webAuthnResult === null) {
            throw new Error('`webAuthnResult` is not available')
          }
          if (webAuthnEncryptedLocalDerivatives === null) {
            throw new Error('`webAuthnEncryptedLocalDerivatives` is not available')
          }
          return <MasterKeyDerivatives>JSON.parse(await getSodiumClient().decryptString(
            webAuthnResult,
            webAuthnEncryptedLocalDerivatives
          ))
        }
        const { kind } = authnInput
        throw new Error(`Unsupported \`AuthnInputKind\`: ${kind}`)
      })
      return concat(
        of(authnViaDepotSignal(indicator(AuthnViaDepotFlowIndicator.COMPUTING_MASTER_KEY_DERIVATIVES))),
        masterKeyDerivativesObservable.pipe(
          switchMap(({ authDigest, encryptionKey }) => {
            if (authDigest !== credentials.hash) {
              return of(authnViaDepotSignal(failure(AuthnViaDepotFlowError.INVALID_CREDENTIALS)))
            }
            return concat(
              of(authnViaDepotSignal(indicator(AuthnViaDepotFlowIndicator.DECRYPTING_DATA))),
              forkJoin([
                getSodiumClient().decryptString(encryptionKey, state.depot.vault!),
                iif<null, string>(
                  () => state.depot.encryptedOtpToken === null,
                  Promise.resolve((null)),
                  defer(() => getSodiumClient().decryptString(encryptionKey, state.depot.encryptedOtpToken))
                )
              ]).pipe(
                switchMap(([vault, otpToken]) => of(authnViaDepotSignal(success({
                  username: action.payload.username,
                  authnInput: action.payload.authnInput,
                  userKeys: (<DeepPartial<Key>[]>JSON.parse(vault)).map((keyPartial): Key => ({
                    identifier: keyPartial.identifier!,
                    value: keyPartial.value!,
                    tags: keyPartial.tags!,
                    attrs: {
                      isShadow: keyPartial.attrs?.isShadow || false,
                      parent: normalizeParentId(keyPartial.attrs?.parent),
                      isPinned: keyPartial.attrs?.isPinned || false
                    },
                    creationTimeInMillis: keyPartial.creationTimeInMillis || 0
                  })),
                  depotKey: encryptionKey,
                  otpToken: otpToken
                }))))
              )
            )
          })
        )
      ).pipe(
        catchError((error) => of(authnViaDepotSignal(exception(errorToMessage(error)))))
      )
    } else if (isActionOf(authnViaDepotReset, action)) {
      return of(authnViaDepotSignal(cancel()))
    }
    return EMPTY
  })
)

export const displayAuthnViaDepotExceptionsEpic = createDisplayExceptionsEpic(authnViaDepotSignal)

export const backgroundRemoteAuthnEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) => action$.pipe(
  filter(isActionOf(initiateBackgroundAuthn)),
  withLatestFrom(state$),
  switchMap(([action, state]) => {
    const { username, authnInput } = action.payload
    if (authnInput.kind === AuthnInputKind.PASSWORD) {
      return apiAuthn(backgroundRemoteAuthnSignal, {
        username,
        password: authnInput.password
      })
    }
    if (authnInput.kind === AuthnInputKind.WEB_AUTHN) {
      const { depot } = state
      const { webAuthnResult, webAuthnEncryptedRemoteDerivatives } = depot
      if (webAuthnResult === null) {
        return of(backgroundRemoteAuthnSignal(exception('`webAuthnResult` is not available')))
      }
      if (depot.webAuthnEncryptedRemoteDerivatives === null) {
        return of(backgroundRemoteAuthnSignal(failure(ServiceLogInResponseError.INVALIDCREDENTIALS)))
      }
      return concat(
        of(backgroundRemoteAuthnSignal(indicator(AuthnViaApiFlowIndicator.RETRIEVING_PARAMETRIZATION))),
        from(getAuthenticationApi().authenticationFetchSalt({ username })).pipe(
          switchMap((getSaltResponse: ServiceGetSaltResponse) => {
            switch (getSaltResponse.error) {
              case ServiceGetSaltResponseError.NONE:
                return concat(
                  of(backgroundRemoteAuthnSignal(indicator(AuthnViaApiFlowIndicator.COMPUTING_MASTER_KEY_DERIVATIVES))),
                  from(getSodiumClient().decryptString(webAuthnResult, webAuthnEncryptedRemoteDerivatives)).pipe(
                    map((decrypted) => JSON.parse(decrypted) as MasterKeyDerivatives),
                    switchMap(({ authDigest, encryptionKey }) => logInRpc(backgroundRemoteAuthnSignal, {
                      username,
                      authnInput,
                      parametrization: getSaltResponse.salt!,
                      authDigest,
                      encryptionKey
                    }))
                  )
                )
              default:
                return of(backgroundRemoteAuthnSignal(failure(getSaltResponse.error!)))
            }
          })
        )
      ).pipe(
        catchError((error) => of(backgroundRemoteAuthnSignal(exception(errorToMessage(error)))))
      )
    }
    const { kind } = authnInput
    throw new Error(`Unsupported \`AuthnInputKind\`: ${kind}`)
  })
)

export const backgroundOtpProvisionEpic: Epic<RootAction, RootAction, RootState> = (action$, state$) => action$.pipe(
  filter(isActionSuccess(backgroundRemoteAuthnSignal)),
  withLatestFrom(state$),
  switchMap(([action, state]) => {
    const { data } = action.payload
    return fn.pipe(
      option.getLeft(data.content),
      option.chain((otpContext) => fn.pipe(
        option.fromNullable(state.depot.depotKey),
        option.map((depotKey) => fn.pipe(
          option.fromNullable(state.depot.encryptedOtpToken),
          option.map((encryptedOtpToken) => from(
            getSodiumClient().decryptString(depotKey, encryptedOtpToken)
          ).pipe(
            switchMap((otpToken) => otpProvision({
              credentialParams: {
                username: data.username,
                authnInput: data.authnInput,
                parametrization: data.parametrization,
                authDigest: data.authDigest,
                encryptionKey: data.encryptionKey
              },
              authnKey: otpContext.authnKey,
              otp: otpToken,
              yieldTrustedToken: true
            }, backgroundOtpProvisionSignal))
          )),
          option.getOrElse<Observable<RootAction>>(() => of(localOtpTokenFailure()))
        ))
      )),
      option.getOrElse<Observable<RootAction>>(() => EMPTY)
    )
  })
)

export const backgroundAuthnErrorEpic: Epic<RootAction, RootAction, RootState> = (action$) => action$.pipe(
  filter(isActionOf([backgroundRemoteAuthnSignal, backgroundOtpProvisionSignal])),
  switchMap((action) => {
    if (isSignalError(action.payload)) {
      return of(backgroundAuthnError())
    }
    return EMPTY
  })
)

export const remoteCredentialsMismatchLocalEpic: Epic<RootAction, RootAction, RootState> = (action$) => action$.pipe(
  filter(isActionOf(backgroundRemoteAuthnSignal)),
  map((action) => action.payload),
  filter(isSignalFailure),
  filter((signal) => signal.error.value === ServiceLogInResponseError.INVALIDCREDENTIALS),
  mapTo(remoteCredentialsMismatchLocal())
)

export const localOtpTokenFailureEpic: Epic<RootAction, RootAction, RootState> = (action$) => action$.pipe(
  filter(isActionOf(backgroundOtpProvisionSignal)),
  map((action) => action.payload),
  filter(isSignalFailure),
  filter((signal) => signal.error.value.error === ServiceProvideOtpResponseError.INVALIDCODE),
  mapTo(localOtpTokenFailure())
)

export const remoteAuthnCompleteOnCredentialsEpic: Epic<RootAction, RootAction, RootState> = (action$) => action$.pipe(
  filter(isActionSuccess2([authnViaApiSignal, backgroundRemoteAuthnSignal])),
  concatMap((action) => fn.pipe(
    option.of(action.payload.data),
    option.chain((data) => fn.pipe(
      option.getRight(data.content),
      option.map((content) => of(remoteAuthnComplete({
        username: data.username,
        authnInput: data.authnInput,
        parametrization: data.parametrization,
        authDigest: data.authDigest,
        encryptionKey: data.encryptionKey,
        ...content,
        isOtpEnabled: false,
        otpToken: null
      })))
    )),
    option.getOrElse<Observable<RootAction>>(() => EMPTY)
  ))
)

export const remoteAuthnCompleteOnOtpEpic: Epic<RootAction, RootAction, RootState> = (action$) => action$.pipe(
  filter(isActionSuccess2([authnOtpProvisionSignal, backgroundOtpProvisionSignal])),
  map((action) => remoteAuthnComplete({
    ...action.payload.data.credentialParams,
    ...action.payload.data.userData,
    isOtpEnabled: true,
    otpToken: fn.pipe(
      action.payload.data.trustedToken,
      option.getOrElse<string | null>(() => null)
    )
  }))
)
